import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminCheck } from "@/hooks/use-admin-check";
import { Loader2, ShieldAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { logSecurityEvent } from "@/lib/audit-logger";

export function AdminGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminCheck();
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (!isLoading && !data?.isAdmin) {
      // Log unauthorized access attempt
      logSecurityEvent({
        event_type: 'access_denied',
        severity: 'high',
        resource: 'admin_area',
        action: 'access_attempt',
        metadata: {
          attempted_route: window.location.pathname,
          has_auth: !!data,
        },
      });
      
      setAccessDenied(true);
      
      // Redirect after showing alert
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 3000);
    }
  }, [data, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Alert variant="destructive" className="max-w-md">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You do not have permission to access this area. This incident has been logged.
            Redirecting...
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!data?.isAdmin) {
    return null;
  }

  return <>{children}</>;
}
